import Select from 'react-select'
import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { faBolt } from '@fortawesome/pro-regular-svg-icons'

import { lib } from '@gocanvas/client'
const { orderByPosition, referenceDataHooks } = lib
const { useReferenceDataColumn } = referenceDataHooks

import { updateEntry } from 'apps/builder/store/actions/entries'
import { selectStyles } from 'apps/builder/components/shared'

import { ClearAll } from 'apps/builder/components/choices/ClearAll'
import { EditableToggle } from 'apps/builder/components/choices/EditableToggle'
import { DefaultValue } from 'apps/builder/components/choices/DefaultValue'
import { ChoiceType } from 'apps/builder/components/choices/ChoiceType'
import { ChoiceOption } from 'apps/builder/components/choices/ChoiceOption'
import { TypeMyOwn } from 'apps/builder/components/choices/TypeMyOwn'
import { commonListOptions } from 'apps/builder/components/choices/CommonList'

import { actions } from 'apps/builder/store/reducers/referenceData'
import { builderTypeSelector } from 'apps/builder/store/selectors'

const { addReferenceDataOptions } = actions

export const ReferenceDataPreview = ({
  reference_data_id,
  reference_data_column,
  showDuplicates = false,
}) => {
  const {
    name,
    columnIndex = 0,
    rows = [],
  } = useReferenceDataColumn({
    reference_data_id,
    reference_data_column,
  })

  const seenValues = new Set()

  return (
    <>
      <div className="option--input option--ref-data">
        <div className="fa-ref-data-icon--wrapper">
          <FontAwesomeIcon icon={faBolt} className="label--icon" />
        </div>
        <div className="option--label">{name}</div>
      </div>
      {reference_data_column && rows.map((row, i) => {
          const { [columnIndex]: value } = row
          const seen = !showDuplicates && seenValues.has(value)
          if (!value || seen) {
            return null
          }
          if (!showDuplicates) {
            seenValues.add(value)
          }
          return (
          <div key={`${reference_data_id}-row-${i}`} className="option--input">
            <div className="option--label">{value}</div>
          </div>
        )})}
    </>
  )
}

const showSpecialOptions = ({ type, style }) => {
  // Dropdown
  if (type == 'Dropdown' && (style == 3 || style == 34)) {
    return true
  }
  // Radio Buttons
  if (type == 'Single Choice' && style == 1) {
    return true
  }

  // Single Choice Boxes - not allowed atm
  // if (type == 'Single Choice' && style == 5) {
  //   return true
  // }

  // Checkboxes only?
  if (type == 'Multiple Choice' && style == 2) {
    return true
  }

  return false
}

const boxStyleOptions = [2, 3, 4, 5].map(option => ({
  value: option,
  label: option,
}))

export const Choices = ({
  entry_values = [],
  guid = '',
  choicesLabel = 'Choices',
  default: defaultValue,
  referenceDataOptions = [],
  style = 1,
  type,
  minimum = 2,
  maximum = 5,
  listType,
  typeMyOwn,
  ...entry
}) => {
  const [focusIndex, setFocusIndex] = useState(0)
  const [boxFocus, setBoxFocus] = useState(false)
  const [displayTypeMyOwnOptions, setDisplayTypeMyOwnOptions] =
    useState(typeMyOwn)
  const dispatch = useDispatch()
  const { reference_data_id, reference_data_column } = entry
  const showDefaultValue = style != 6
  const hasEditableToggle =
    (type == 'Dropdown' && (style == 3 || style == 34)) || false
  const builderType = useSelector(builderTypeSelector)
  const addSpecial = showSpecialOptions({ type, style })

  const boxStyleDefaultValue = boxStyleOptions.find(
    ({ value }) => value == maximum
  )

  const newDefaultEntries = selectedMaxNum => {
    const entriesNum = selectedMaxNum - entry_values.length

    if (entriesNum >= 0) {
      const totalEntries = [...Array(entriesNum)].map(() => {
        return {
          placeholder: 'Type your choices here and press Enter',
        }
      })
      setFocusIndex(0)
      return [...entry_values, ...totalEntries].map((entry, i) => {
        return { ...entry, position: i } //reorder position
      })
    } else {
      return entry_values.slice(0, entriesNum)
    }
  }

  const options =
    entry_values.length > 0
      ? entry_values
      : style == 5
      ? newDefaultEntries()
      : [{ placeholder: 'Type your choices here and press Enter', position: 0 }]

  const updateOptions = option => {
    const { position } = option

    if (style == 5 && position == 0 && !option.text) {
      dispatch(
        updateEntry({
          guid,
          entry_values: [option],
        })
      )
    } else {
      const otherOptions = options.filter(
        option => option.position !== position
      )

      const newOptions = [...otherOptions, option].sort(orderByPosition)

      dispatch(
        updateEntry({
          guid,
          entry_values: newOptions.filter(({ text }) => text),
        })
      )
    }
  }

  const bulkUpdateOptions = options => {
    dispatch(
      updateEntry({
        guid,
        entry_values: options,
      })
    )
  }

  const deleteOption = ({ position }) => {
    if (style != 5 || entry_values.length > 1) {
      setFocusIndex(position - 1)

      const otherOptions = options
        .filter(option => option.position != position)
        .map((entry, i) => ({
          ...entry,
          position: i,
        }))

      dispatch(
        updateEntry({
          guid,
          entry_values: otherOptions,
        })
      )
    }
  }

  const reIndexChoices = (insertPos, newOption) => {
    let newOptions = [...options]

    /** Insert new option into array */
    newOptions.splice(insertPos, 0, newOption)

    const reIndexedOptions = newOptions.map((options, index) => {
      /** Re-index other opts */
      if (options.position != index) {
        return {
          ...options,
          position: index,
        }
        /** return unmodified option */
      } else {
        return options
      }
    })

    return reIndexedOptions
  }

  const addNewOption = (insertPos = options.length) => {
    setFocusIndex(insertPos)
    let newOptions = []
    const placeHolderOptions = {
      placeholder: 'Type your choice here and press Enter',
      position: insertPos,
    }
    const shouldReIndex = insertPos <= options.length

    if (style == 5) {
      if (entry_values.length < maximum) {
        if (shouldReIndex) {
          newOptions = reIndexChoices(insertPos, placeHolderOptions)
        } else {
          newOptions = [
            ...options,
            {
              placeHolderOptions,
            },
          ]
        }
        dispatch(
          updateEntry({
            guid,
            entry_values: newOptions,
          })
        )
      }
    } else {
      if (shouldReIndex) {
        newOptions = reIndexChoices(insertPos, placeHolderOptions)
      } else {
        newOptions = [
          ...options,
          {
            placeHolderOptions,
          },
        ]
      }
      dispatch(
        updateEntry({
          guid,
          entry_values: newOptions,
        })
      )
    }
  }

  const addCommonListEntries = values => {
    dispatch(
      updateEntry({
        guid,
        entry_values: values,
        typeMyOwn: 'edit-common-list',
      })
    )
  }

  const onPaste = (position) => (event) => {
    const { value } = event.target
    const hasValue = String(value).length//not trimmed, allowing whitespace option
    const offset = hasValue ? 1 : 0
    const pastedText = event.clipboardData.getData('text')

    // only override the default behavior if there are newlines
    if (pastedText && pastedText.includes('\n')){
      const pastedLines = pastedText.split(/\n+/).map(
        (item, index) => ({
          text: item.trim()
        })
      )
      const newOptionsLn = pastedLines.length

      let combinedLines = [...entry_values]
      combinedLines.splice(position + offset, 0, ...pastedLines)
      // only apply the field maximum to the boxes style
      if (type === 'Single Choice' && style == 5){
        combinedLines.length = Math.min(combinedLines.length, maximum)
      }
      // 15 really should be the field default, per builder v2
      // if (type === 'Multiple Choice'){
      //   combinedLines.length = Math.min(combinedLines.length, 15)
      // }
      // reindex all positions
      bulkUpdateOptions(
        combinedLines.map((o, i) => ({...o, position: i}))
      )
      setFocusIndex(position + offset + newOptionsLn - 1)
      event.preventDefault()
    }
  }

  const StartFromScratch = () => {
    return (
      <div className={`choice--options ${style == 5 ? 'no-scroll' : ''}`}>
        {options.map((option = {}, i) => {
          const update = value => {
            let updatedOption = {
              ...option,
              position: i,
              text: value,
            }
            if (entry_values.length) {
              const values = entry_values.map(e => {
                return e.position == i ? updatedOption : e
              })
              bulkUpdateOptions(values)
            } else {
              updateOptions(updatedOption)
            }
            dispatch(updateEntry({ guid, typeMyOwn: 'start-from-scratch' }))
          }

          return (
            <ChoiceOption
              {...option}
              key={`${guid}-option-${i}`}
              onFocus={() => setFocusIndex(i)}
              focus={i === focusIndex}
              onChange={update}
              onDelete={() => deleteOption(option)}
              onPaste={onPaste(i)}
              onEnter={() => addNewOption(i + 1)}
            />
          )
        })}
      </div>
    )
  }

  const CommonListChoices = () => {
    return (
      <div className="common-list-container">
        {!entry_values.length ? (
          <div className="common-list-choices-wrapper">
            {commonListOptions.map(option => {
              return (
                <div
                  key={option.description}
                  onClick={() => addCommonListEntries(option.values)}
                >
                  {option.description}
                  <FontAwesomeIcon
                    className="fields-library--toggle-icon"
                    icon={faChevronRight}
                  />
                </div>
              )
            })}
          </div>
        ) : (
          <div className="common-list-options-wrapper">
            {entry_values.map((option = {}, i) => {
              const update = value => {
                let updatedOption = {
                  ...option,
                  position: i,
                  text: value,
                }
                if (entry_values.length) {
                  const values = entry_values.map(e => {
                    return e.position == i ? updatedOption : e
                  })
                  bulkUpdateOptions(values)
                } else {
                  updateOptions(updatedOption)
                }
              }
              return (
                <ChoiceOption
                  {...option}
                  key={`${guid}-option-${i}`}
                  focus={i === focusIndex}
                  onFocus={() => setFocusIndex(i)}
                  onChange={update}
                  onPaste={onPaste(i)}
                  onDelete={() => deleteOption(option)}
                  onEnter={() => addNewOption(i + 1)}
                />
              )
            })}
          </div>
        )}
      </div>
    )
  }

  const ChoiceOptions = () => {
    if (reference_data_id) {
      const referencePreviewProps = {
        reference_data_id,
        reference_data_column,
        update: options => {
          dispatch(
            addReferenceDataOptions({
              reference_data_id,
              reference_data_column,
              referenceDataOptions: options,
            })
          )
        },
      }
      return <ReferenceDataPreview {...referencePreviewProps} />
    }

    if (!displayTypeMyOwnOptions && !entry_values.length) {
      return (
        <TypeMyOwn
          setDisplayTypeMyOwnOptions={setDisplayTypeMyOwnOptions}
          guid={guid}
        />
      )
    }

    if (displayTypeMyOwnOptions == 'start-from-scratch') {
      return StartFromScratch()
    }

    return CommonListChoices()
  }

  return (
    <div className="choices--field">
      <div className="choices--column">
        <div className="label-floating">
          <div
            className={`field--options input--placeholder settings--choice-wrapper
            ${boxFocus ? 'field--options-focus' : ''}`}
          >
            <ChoiceOptions />

            {options.length > 1 && !reference_data_id && (
              <ClearAll
                style={style}
                maximum={maximum}
                guid={guid}
                setBoxFocus={setBoxFocus}
                setDisplayTypeMyOwnOptions={setDisplayTypeMyOwnOptions}
              />
            )}
          </div>

          <label className={`${boxFocus ? 'field--label-focus' : ''}`}>
            {choicesLabel}
          </label>
        </div>
        {showDefaultValue && (
          <DefaultValue
            entry_values={entry_values}
            guid={guid}
            options={options}
            addSpecial={addSpecial}
            hasReferenceData={reference_data_id}
            defaultValue={defaultValue}
            reference_data_id={reference_data_id}
            reference_data_column={reference_data_column}
          />
        )}
        {builderType == 'builder' && hasEditableToggle && (
          <EditableToggle guid={guid} style={style} />
        )}
      </div>
      <div className="choices--column">
        {style == 5 ? (
          <div className="label-floating">
            <Select
              styles={selectStyles}
              className="input--placeholder default-value--select"
              classNamePrefix="react-select"
              onChange={({ value }) => {
                dispatch(
                  updateEntry({
                    guid,
                    maximum: value,
                    entry_values: newDefaultEntries(value),
                    default: defaultValue,
                  })
                )
              }}
              options={boxStyleOptions}
              value={boxStyleDefaultValue}
            />
            <label className="label-default-value">Number of Choices</label>
          </div>
        ) : (
          <ChoiceType listType={listType} guid={guid} />
        )}
      </div>
    </div>
  )
}
